// ExportPopover.jsx
import React, { useState, useEffect } from 'react';
import {
  useFloating,
  offset,
  flip,
  shift,
  FloatingPortal,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
} from '@floating-ui/react';
import {
   ClipboardDocumentListIcon,
  ClipboardDocumentIcon,
  ArrowUpOnSquareIcon,
  PhotoIcon,
  DocumentChartBarIcon,
  ChevronDownIcon
} from "@heroicons/react/24/outline";

import { motion, AnimatePresence } from 'framer-motion';
import ExportButton from './ExportButton'; // Reusable ExportButton component

const ExportPopover = ({
  onCopyImage,
  onCopyData,
  onExportPNG,
  onExportExcel,
}) => {
  const [isCopyImageSupported, setIsCopyImageSupported] = useState(false);
  const [open, setOpen] = useState(false);

  const { x, y, strategy, refs, context } = useFloating({
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    open,
    onOpenChange: setOpen,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  // Check image copy support on mount
  useEffect(() => {
    const support = checkCopyImageSupport();
    setIsCopyImageSupported(support);
  }, []);

  // Check if copy image is supported
  const checkCopyImageSupport = () => {
    if (typeof navigator === 'undefined') {
      return false;
    }

    const userAgent = navigator.userAgent;
    const isChrome = /Chrome/.test(userAgent) && !/Edg/.test(userAgent);
    const isEdge = /Edg/.test(userAgent);
    return isChrome || isEdge;
  };

  // Function to handle action completion
  const handleActionComplete = () => {
    setOpen(false);
  };

  return (
    <div className="relative">

<button
  ref={refs.setReference}
  {...getReferenceProps({
    className: `inline-flex items-center justify-between w-full 
                px-4 py-2 text-sm font-medium text-zinc-700 
                rounded-lg bg-zinc-100 hover:bg-zinc-200/70 
                focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-75`,
    'aria-expanded': open,
    'aria-haspopup': 'true',
  })}
>
  <div
    
    className="flex items-center space-x-2"
  >
    <ArrowUpOnSquareIcon className="h-4 w-4 text-zinc-500" aria-hidden="true" />
    <span className="font-medium text-zinc-500 pr-5">Copy & Export Options</span>
    <motion.div
      initial={{ rotate: 0 }}
      animate={{ rotate: open ? 180 : 0 }}
      transition={{ duration: 0.2, ease: "easeInOut" }}
    >
      <ChevronDownIcon className="h-3 w-3 text-zinc-700" aria-hidden="true" />
    </motion.div>
  </div>
</button>


      <FloatingPortal>
        <AnimatePresence>
          {open && (
            <motion.div
              ref={refs.setFloating}
              {...getFloatingProps({
                className: "z-50 mt-2 w-72 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
                style: {
                  position: strategy,
                  top: y ?? 0,
                  left: x ?? 0,
                },
              })}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
            >
              <div className="p-4 relative">
                {/* Copy to Clipboard Category */}
                <div className="mb-4 mt-2">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Copy to Clipboard
                  </h3>
                  <div className="">
                    <ExportButton
                      onClick={onCopyData}
                      disabled={false}
                      title={null}
                      Icon={ClipboardDocumentListIcon}
                      onActionComplete={handleActionComplete}
                    >
                      Table
                    </ExportButton>

                    <ExportButton
                      onClick={isCopyImageSupported ? onCopyImage : null}
                      disabled={!isCopyImageSupported}
                      title={
                        !isCopyImageSupported
                          ? 'Copying chart images is only supported in Chrome and Edge browsers.'
                          : null
                      }
                      Icon={ClipboardDocumentIcon}
                      onActionComplete={handleActionComplete}
                    >
                      Image
                    </ExportButton>
                  </div>
                </div>

                {/* Export Category */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    Export
                  </h3>
                  <div className="">
                    <ExportButton
                      onClick={onExportExcel}
                      disabled={false}
                      Icon={DocumentChartBarIcon}
                      onActionComplete={handleActionComplete}
                    >
                      Excel Document
                    </ExportButton>

                    <ExportButton
                      onClick={onExportPNG}
                      disabled={false}
                      Icon={PhotoIcon}
                      onActionComplete={handleActionComplete}
                    >
                      Image
                    </ExportButton>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </FloatingPortal>
    </div>
  );
};

export default ExportPopover;
